import { Flex, ThemeUIStyleObject } from 'theme-ui';
import { motion } from 'framer-motion';
import React, { useEffect, useRef } from 'react';
import ProductLink from '~/components/Product/Link';
import ImgLoader from '~/components/Generic/ImgLoader';
import { alpha } from '@theme-ui/color';
import { useLocation } from '@reach/router';

const CollectionProductNav = React.memo(function CollectionProductNav({
  collection,
  forwardSx = {},
}: {
  collection: Queries.ProductCollectionFragment[];
  forwardSx?: ThemeUIStyleObject;
}) {
  const collectionName = collection?.[0]?.collection?.[0]?.name;
  const linkRef = useRef<HTMLLIElement>(null);
  const { pathname } = useLocation();
  useEffect(() => {
    linkRef.current?.scrollIntoView({ behavior: 'instant', inline: 'center', block: 'nearest' });
  }, [pathname]);

  return (
    <nav
      key="collection-nav"
      aria-label={`${collectionName}'s products`}
      sx={(t) => ({
        maxWidth: '100%',
        height: 'var(--product-nav-height)',
        overflowX: 'auto',
        overflowY: 'hidden',
        scrollbarWidth: 'none',
        fontFamily: 'heading',
        borderColor: 'cloud',
        backgroundColor: [alpha('backgroundGrey', 0.6)(t), null, 'transparent'],
        backdropFilter: 'saturate(180%) blur(20px)',
        zIndex: 2,
        ...forwardSx,
      })}
    >
      <Flex
        sx={{
          width: '100%',
          top: '0px',
          pt: ['.4rem', '1.6rem'],
          pb: '0.8rem',
          justifyContent: 'center',
        }}
      >
        <Flex
          as="ul"
          sx={{
            '--card-size': ['4rem'],
            variant: 'list.bare',
            position: 'relative',
            flexFlow: 'row nowrap',
            alignItems: 'center',
            gap: ['0.8rem', '2.4rem'],
            px: '1.8rem',
            'li:last-of-type': {
              pr: ['1.8rem', '0'],
            },
            '.is-active > div': {
              opacity: '80% !important',
            },
          }}
        >
          {collection
            ?.filter((product) => product.navigationImage)
            ?.sort((a, b) => (a?.navOrder ?? 0) - (b?.navOrder ?? 0))
            .map(({ navigationImage, ...product }) => {
              return (
                <li
                  ref={pathname.includes(product.slug ?? '') ? linkRef : null}
                  key={product.slug}
                  sx={{ padding: 0, m: 0 }}
                >
                  <ProductLink
                    locationDetail="nav"
                    className="collection-nav-item"
                    productName={product.title}
                    productSlug={product.slug}
                    collectionSlug={product.collection?.[0]?.slug}
                    activeClassName="is-active"
                    partiallyActive={true}
                    sx={{
                      '--hover-transition': 'all 0.3s ease-in-out',
                      display: 'block',
                      fontWeight: 'medium',
                      fontSize: '1.1rem',
                      color: 'black',
                      textTransform: 'uppercase',
                      lineHeight: 1,
                      textAlign: 'center',
                      width: '6rem',
                      height: '5.4rem',
                      '> div > img': {
                        width: 'var(--card-size)',
                        height: 'var(--card-size)',
                      },
                      ':hover': {
                        'svg path[fill]': {
                          transition: 'opacity 0.3s ease-in-out',
                        },
                      },
                    }}
                    key={product.id}
                  >
                    <motion.div
                      whileHover={{ opacity: 0.8 }}
                      style={{
                        opacity: 0.25,
                        display: 'flex',
                        flexFlow: 'column nowrap',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: '0.2rem',
                      }}
                      transition={{ duration: 0.3 }}
                    >
                      {navigationImage?.file?.contentType?.includes('svg') ? (
                        <img src={navigationImage.url ?? ''} alt={navigationImage.title ?? ''} />
                      ) : (
                        <ImgLoader image={navigationImage} sx={{ width: '100%', height: '100%' }} />
                      )}
                      {product.title?.replace(/omorpho/i, '')}
                    </motion.div>
                  </ProductLink>
                </li>
              );
            })}
        </Flex>
      </Flex>
    </nav>
  );
});

export default CollectionProductNav;
